"use client";

import * as React from "react";
import {
  motion,
  useMotionValue,
  useReducedMotion,
  useSpring,
} from "framer-motion";

import { cn } from "@/lib/utils";

// Animated starfield background, ported from 21st.dev/community/components/
// bundui/stars. Three box-shadow star layers scroll upward on a loop (parallax
// depth via different speeds/sizes) and the whole field drifts gently opposite
// the cursor (the component's default behaviour). Adapted to JS + framer-motion
// (original used motion/react) and made reduced-motion aware: the cursor drift
// is skipped in JS (an effect, so it cannot affect the server render) and the
// scroll loop is frozen in CSS (see the layer's motion-reduce class).

function generateStars(count, starColor) {
  const shadows = [];
  for (let i = 0; i < count; i++) {
    const x = Math.floor(Math.random() * 4000) - 2000;
    const y = Math.floor(Math.random() * 4000) - 2000;
    shadows.push(`${x}px ${y}px ${starColor}`);
  }
  return shadows.join(", ");
}

function StarLayer({
  count = 1000,
  size = 1,
  duration = 50,
  starColor = "#fff",
  className,
  ...props
}) {
  const [boxShadow, setBoxShadow] = React.useState("");

  React.useEffect(() => {
    setBoxShadow(generateStars(count, starColor));
  }, [count, starColor]);

  return (
    <motion.div
      data-slot="star-layer"
      // The drift is declared for everyone and frozen in CSS for visitors who
      // ask for reduced motion. A media query decides that at paint time, so the
      // server and the client render the same markup; branching the `animate`
      // prop on a client-only query put a different transform in the SSR HTML.
      // The `!` is what lets the class beat framer's inline transform.
      animate={{ y: [0, -2000] }}
      transition={{ repeat: Infinity, duration, ease: "linear" }}
      className={cn(
        "absolute left-0 top-0 h-[2000px] w-full motion-reduce:transform-none!",
        className,
      )}
      {...props}
    >
      <div
        className="absolute rounded-full bg-transparent"
        style={{ width: `${size}px`, height: `${size}px`, boxShadow }}
      />
      <div
        className="absolute top-[2000px] rounded-full bg-transparent"
        style={{ width: `${size}px`, height: `${size}px`, boxShadow }}
      />
    </motion.div>
  );
}

export default function StarsBackground({
  children,
  className,
  factor = 0.05,
  speed = 50,
  transition = { stiffness: 50, damping: 20 },
  starColor = "#fff",
  ...props
}) {
  const reduce = useReducedMotion();
  const offsetX = useMotionValue(1);
  const offsetY = useMotionValue(1);

  const springX = useSpring(offsetX, transition);
  const springY = useSpring(offsetY, transition);

  const handleMouseMove = React.useCallback(
    (e) => {
      if (reduce) return;
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;
      offsetX.set(-(e.clientX - centerX) * factor);
      offsetY.set(-(e.clientY - centerY) * factor);
    },
    [offsetX, offsetY, factor, reduce],
  );

  return (
    <div
      data-slot="stars-background"
      className={cn(
        "relative size-full overflow-hidden bg-[radial-gradient(ellipse_at_bottom,_#1a1a1a_0%,_#000_100%)]",
        className,
      )}
      onMouseMove={handleMouseMove}
      {...props}
    >
      <motion.div style={{ x: springX, y: springY }}>
        <StarLayer count={1000} size={1} duration={speed} starColor={starColor} />
        <StarLayer count={400} size={2} duration={speed * 2} starColor={starColor} />
        <StarLayer count={200} size={3} duration={speed * 3} starColor={starColor} />
      </motion.div>
      {children}
    </div>
  );
}
